import { useMemo, useState } from "react";

import type { RequestorIdentity } from "@dfp/react-api-core";
import {
  APIProvider,
  APIRequestorIdentityProvider,
  SplashScreen,
  makeRequestorIdentityDefault,
  makeRequestorIdentityDefaultOnBehalfOf,
  useOnBehalfOfQueryState,
} from "@dfp/react-api-core";
import { AuthProvider, useMsalAccountUsername } from "@dfp/react-msal-support";
import { getAnalyticsTrackingParamsForAD } from "@dfp/react-web-analytics";

import { ADB2C_SCOPES, DEMO_MODE } from "../config.ts";
import { DemoUserAuthProvider } from "../demo/index.ts";

type UserAuthProviderProps = {
  children: React.ReactNode;
  extraQueryParameters?: Record<string, string>;
};

export function UserAuthProvider({
  children,
  extraQueryParameters,
}: UserAuthProviderProps) {
  if (DEMO_MODE) {
    return <DemoUserAuthProvider>{children}</DemoUserAuthProvider>;
  }

  return (
    <AuthProvider
      adb2cScopes={ADB2C_SCOPES}
      authenticationRequest={{
        extraQueryParameters: {
          ...getAnalyticsTrackingParamsForAD(),
          ...extraQueryParameters,
        },
      }}
      LoadingComponent={SplashScreen}
    >
      <APIProvider>
        <DefaultRequestorIdentity>{children}</DefaultRequestorIdentity>
      </APIProvider>
    </AuthProvider>
  );
}

type DefaultRequestorIdentityProps = {
  children: React.ReactNode;
};
function DefaultRequestorIdentity({ children }: DefaultRequestorIdentityProps) {
  const userName = useMsalAccountUsername();
  const [onBehalfOf] = useOnBehalfOfQueryState();
  const [preservedOnBehalfOf] = useState(onBehalfOf);

  const requestorIdentity = useMemo<RequestorIdentity | null>(() => {
    if (preservedOnBehalfOf) {
      return makeRequestorIdentityDefaultOnBehalfOf(
        preservedOnBehalfOf.userName,
        preservedOnBehalfOf.partyRoleReference,
      );
    }

    return makeRequestorIdentityDefault(userName);
  }, [preservedOnBehalfOf, userName]);

  if (requestorIdentity === null) {
    return null;
  }

  return (
    <APIRequestorIdentityProvider requestorIdentity={requestorIdentity}>
      {children}
    </APIRequestorIdentityProvider>
  );
}
